import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { useWorkflow } from "../../context/WorkflowContext";
import {
    Loader2,
    Key,
    Plus,
    Link2,
    Unlink,
    Eye,
    EyeOff,
    AlertCircle,
    Check,
    Globe,
    Server,
} from "lucide-react";
import { toast } from "sonner";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@shadcn-in-nx/ui/alert-dialog";
import { useChatContext } from "../ChatContext";

export default function SecretsPanel() {
    const { t } = useTranslation();
    const { workflow } = useWorkflow();
    const { environments, selectedEnvironment } = useChatContext();
    const [workflowSecrets, setWorkflowSecrets] = useState([]);
    const [allSecrets, setAllSecrets] = useState([]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isAssociateDialogOpen, setIsAssociateDialogOpen] = useState(false);
    const [showValue, setShowValue] = useState(false);
    const [editingSecret, setEditingSecret] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        value: "",
        description: "",
    });
    const [errors, setErrors] = useState({});
    const [selectedSecretToAssociate, setSelectedSecretToAssociate] =
        useState("");

    // Dialog-specific environment state (separate from context's selectedEnvironment)
    const [dialogEnvironment, setDialogEnvironment] = useState("production");

    // API hooks
    const {
        call: fetchSecrets,
        isLoading: isLoadingSecrets,
        error: secretsError,
    } = useBluelagoon({ method: "getSecrets" });

    const { call: upsertSecret, isLoading: isCreating } = useBluelagoon({
        method: "upsertSecret",
    });

    const { call: associateSecret, isLoading: isAssociating } = useBluelagoon({
        method: "associateSecretWithWorkflow",
    });

    const { call: removeSecret } = useBluelagoon({
        method: "removeSecretFromWorkflow",
    });

    const { call: removeSecretFromEnv } = useBluelagoon({
        method: "removeSecretFromEnvironment",
    });

    // Fetch all secrets and filter for this workflow
    useEffect(() => {
        if (workflow?.id) {
            loadSecrets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflow?.id]);

    const loadSecrets = async () => {
        try {
            const response = await fetchSecrets({});
            if (response?.data?.secrets) {
                setAllSecrets(response.data.secrets);
                // Filter secrets that are associated with this workflow
                // Also filter environmentsWithValues to only include current workflow's environments
                const associated = response.data.secrets
                    .filter((secret) =>
                        secret.workflows?.some((w) => w.id === workflow?.id)
                    )
                    .map((secret) => ({
                        ...secret,
                        // Filter environments to only show those belonging to current workflow
                        environmentsWithValues:
                            secret.environmentsWithValues?.filter(
                                (env) => env.workflowId === workflow?.id
                            ) || [],
                    }));
                setWorkflowSecrets(associated);
            }
        } catch (err) {
            console.error("Error fetching secrets:", err);
        }
    };

    // Get secrets not yet associated with this workflow
    const availableSecrets = allSecrets.filter(
        (secret) => !secret.workflows?.some((w) => w.id === workflow?.id)
    );

    // Check if environments feature is available (user has environments)
    const hasEnvironmentsFeature = environments && environments.length > 0;

    // Check if context's selectedEnvironment is production
    // Context provides: undefined/"production" for production, or environment name for others
    const isContextProduction = useMemo(() => {
        return (
            !selectedEnvironment ||
            selectedEnvironment === "production" ||
            selectedEnvironment === "prod"
        );
    }, [selectedEnvironment]);

    // Get the environment ID for the context's selected environment (for remove functionality)
    const contextEnvironmentId = useMemo(() => {
        if (isContextProduction) {
            return "production";
        }
        // Find the environment by name (context uses name, not ID)
        const env = environments?.find(
            (e) => e.name.toLowerCase() === selectedEnvironment?.toLowerCase()
        );
        return env?.id || "production";
    }, [isContextProduction, selectedEnvironment, environments]);

    // Build environment options including "production" as default
    const environmentOptions = useMemo(() => {
        const options = [
            { id: "production", name: "Production", type: "PRODUCTION" },
        ];

        // Add workflow environments (excluding production if it exists)
        for (const env of environments || []) {
            if (env.name.toLowerCase() !== "production") {
                options.push({
                    id: env.id,
                    name: env.name.charAt(0).toUpperCase() + env.name.slice(1),
                    type: env.type,
                });
            }
        }

        return options;
    }, [environments]);

    // Get current environment label for UI (based on context's selectedEnvironment)
    const currentEnvironmentLabel = useMemo(() => {
        if (isContextProduction) {
            return t("secretsPanel.production") || "Production";
        }
        // Find by name since context uses environment name
        const env = environments?.find(
            (e) => e.name.toLowerCase() === selectedEnvironment?.toLowerCase()
        );
        return env?.name
            ? env.name.charAt(0).toUpperCase() + env.name.slice(1)
            : selectedEnvironment || "Production";
    }, [isContextProduction, selectedEnvironment, environments, t]);

    // Check if a secret has a value for a specific environment
    const hasValueForEnvironment = (secret, envName) => {
        if (envName === "production") {
            return secret.hasProductionValue;
        }
        return secret.environmentsWithValues?.some(
            (e) => e.name.toLowerCase() === envName.toLowerCase()
        );
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name) {
            newErrors.name = t("secrets.nameRequired");
        } else if (!/^[A-Z][A-Z0-9_]*$/.test(formData.name)) {
            newErrors.name = t("secrets.nameFormat");
        }

        if (!formData.value && !editingSecret) {
            newErrors.value = t("secrets.valueRequired");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveSecret = async () => {
        if (!validateForm()) return;

        try {
            const isDialogProduction = dialogEnvironment === "production";

            const payload = {
                name: formData.name,
                description: formData.description || undefined,
            };

            // Only include value if it's provided (for updates, empty value means keep existing)
            if (formData.value) {
                payload.value = formData.value;
            } else if (editingSecret) {
                // If editing and no new value provided, we need to tell the user
                if (!formData.value) {
                    toast.info(
                        t("secrets.valueNotChanged") ||
                            "Secret value not changed"
                    );
                }
                // Still proceed if they want to update description only
                if (
                    !formData.description &&
                    editingSecret.description === formData.description
                ) {
                    // No changes made
                    setIsCreateDialogOpen(false);
                    return;
                }
            }

            // Ensure value is provided for the API if creating new
            if (!editingSecret && !payload.value) {
                setErrors({ value: t("secrets.valueRequired") });
                return;
            }

            // Add environment information for non-production
            if (!isDialogProduction) {
                // dialogEnvironment contains the environment ID
                payload.workflowEnvironmentId = dialogEnvironment;
                payload.workflowId = workflow.id;
            }

            const response = await upsertSecret(payload);

            const envLabel = isDialogProduction
                ? "Production"
                : environmentOptions.find((e) => e.id === dialogEnvironment)
                      ?.name || dialogEnvironment;

            toast.success(
                editingSecret
                    ? t("secretsPanel.secretUpdatedEnv", { env: envLabel }) ||
                          `Secret updated successfully for ${envLabel}`
                    : t("secretsPanel.secretCreatedEnv", { env: envLabel }) ||
                          `Secret created successfully for ${envLabel}`
            );

            // Associate the new secret with the workflow if it's new
            if (!editingSecret) {
                await associateSecret({
                    workflowId: workflow.id,
                    secretId: response.data?.secret?.id,
                });
            }

            // Reload secrets
            await loadSecrets();

            // Reset form and close dialog
            resetCreateForm();
            setIsCreateDialogOpen(false);
        } catch (err) {
            console.error("Error saving secret:", err);
            toast.error(
                err?.message ||
                    (editingSecret
                        ? t("secretsPanel.failedToUpdateSecret") ||
                          "Failed to update secret"
                        : t("secretsPanel.failedToCreateSecret") ||
                          "Failed to create secret")
            );
        }
    };

    const handleEditSecret = (secret) => {
        setEditingSecret(secret);
        setFormData({
            name: secret.name,
            value: "", // Never pre-fill value
            description: secret.description || "",
        });
        setIsCreateDialogOpen(true);
    };

    const handleAssociateSecret = async () => {
        if (!selectedSecretToAssociate) {
            toast.error(
                t("secretsPanel.selectSecret") || "Please select a secret"
            );
            return;
        }

        try {
            await associateSecret({
                workflowId: workflow.id,
                secretId: selectedSecretToAssociate,
            });

            toast.success(
                t("secretsPanel.secretAssociated") ||
                    "Secret associated successfully"
            );

            // Reload secrets
            await loadSecrets();

            // Reset and close dialog
            setSelectedSecretToAssociate("");
            setIsAssociateDialogOpen(false);
        } catch (err) {
            console.error("Error associating secret:", err);
            toast.error(
                err?.message ||
                    t("secretsPanel.failedToAssociateSecret") ||
                    "Failed to associate secret"
            );
        }
    };

    const handleRemoveSecret = async (secretId, secretName) => {
        try {
            // If no environments feature or production, remove from workflow entirely
            if (!hasEnvironmentsFeature || isContextProduction) {
                await removeSecret({
                    workflowId: workflow.id,
                    secretId,
                });

                toast.success(
                    t("secretsPanel.secretRemoved", { name: secretName }) ||
                        `Secret "${secretName}" removed from workflow`
                );
            } else {
                // Remove only from the specific environment
                // contextEnvironmentId contains the actual environment ID for non-production
                await removeSecretFromEnv({
                    secretId,
                    workflowEnvironmentId: contextEnvironmentId,
                });

                toast.success(
                    t("secretsPanel.secretRemovedFromEnv", {
                        name: secretName,
                        env: currentEnvironmentLabel,
                    }) ||
                        `Secret "${secretName}" removed from ${currentEnvironmentLabel}`
                );
            }

            // Reload secrets
            await loadSecrets();
        } catch (err) {
            console.error("Error removing secret:", err);
            toast.error(
                err?.message ||
                    t("secretsPanel.failedToRemoveSecret") ||
                    "Failed to remove secret"
            );
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Auto-format name to uppercase and replace invalid characters
        if (name === "name") {
            const formattedValue = value
                .toUpperCase()
                .replace(/[^A-Z0-9_]/g, "_");
            setFormData((prev) => ({ ...prev, [name]: formattedValue }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }

        // Clear errors when user types
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const resetCreateForm = () => {
        setFormData({ name: "", value: "", description: "" });
        setErrors({});
        setShowValue(false);
        setEditingSecret(null);
        setDialogEnvironment("production");
    };

    if (isLoadingSecrets) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (secretsError) {
        return (
            <div className="flex h-full items-center justify-center p-6 text-center">
                <div className="space-y-2">
                    <p className="text-sm font-medium text-destructive">
                        {t("secretsPanel.errorLoading") ||
                            "Error loading secrets"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                        {secretsError.message}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col bg-background">
            <div className="border-b p-4 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold">
                            {t("secretsPanel.title") || "Secrets"}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {t("secretsPanel.description") ||
                                "Manage secrets for this workflow. Secrets are encrypted and can be used in your code."}
                        </p>
                    </div>
                </div>

                {/* Action buttons */}
                {workflow?.isFunctionAPIEnabled && (
                    <div className="flex gap-2 flex-wrap">
                        <Button
                            size="sm"
                            onClick={() => {
                                resetCreateForm();
                                setIsCreateDialogOpen(true);
                            }}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            {t("secretsPanel.createNew") || "Create New Secret"}
                        </Button>
                        {availableSecrets.length > 0 && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setIsAssociateDialogOpen(true)}
                            >
                                <Link2 className="w-4 h-4 mr-2" />
                                {t("secretsPanel.associateExisting") ||
                                    "Add Existing Secret"}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            <ScrollArea className="flex-1 p-4">
                {!workflow?.isFunctionAPIEnabled ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="mb-4 rounded-full bg-amber-100 p-3">
                            <AlertCircle className="h-6 w-6 text-amber-600" />
                        </div>
                        <h3 className="text-sm font-medium">
                            {t("secretsPanel.backendFunctionsRequired") ||
                                "Backend Functions Required"}
                        </h3>
                        <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                            {t("secretsPanel.backendFunctionsRequiredDesc") ||
                                "Secrets only work with backend functions. Please enable backend functions in settings to use secrets."}
                        </p>
                    </div>
                ) : workflowSecrets.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="mb-4 rounded-full bg-muted p-3">
                            <Key className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <h3 className="text-sm font-medium">
                            {t("secretsPanel.noSecrets") ||
                                "No secrets configured"}
                        </h3>
                        <p className="mt-1 max-w-xs text-xs text-muted-foreground">
                            {t("secretsPanel.noSecretsDesc") ||
                                "Create a new secret or associate an existing one to use in your workflow."}
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
                        {workflowSecrets.map((secret) => (
                            <Card key={secret.id} className="overflow-hidden">
                                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                                    <div className="space-y-1 flex-1 min-w-0">
                                        <CardTitle className="text-base font-medium flex items-center gap-2">
                                            <Key className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                            <code className="bg-gray-100 px-2 py-0.5 rounded text-sm font-mono truncate">
                                                {secret.name}
                                            </code>
                                        </CardTitle>
                                        <CardDescription className="text-xs truncate">
                                            {secret.description ||
                                                t("secrets.noDescription") ||
                                                "No description"}
                                        </CardDescription>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                                handleEditSecret(secret)
                                            }
                                            className="text-muted-foreground hover:text-foreground flex-shrink-0"
                                            title={
                                                t("secrets.editSecret") ||
                                                "Edit Secret"
                                            }
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="lucide lucide-pencil"
                                            >
                                                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                                <path d="m15 5 4 4" />
                                            </svg>
                                        </Button>
                                        {/* Only show unlink button if:
                                            - Production is selected (can always unlink from workflow)
                                            - OR non-production is selected AND secret has value for that environment */}
                                        {(isContextProduction ||
                                            hasValueForEnvironment(
                                                secret,
                                                selectedEnvironment
                                            )) && (
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                                                        title={
                                                            hasEnvironmentsFeature &&
                                                            !isContextProduction
                                                                ? t(
                                                                      "secretsPanel.removeFromEnv",
                                                                      {
                                                                          env: currentEnvironmentLabel,
                                                                      }
                                                                  ) ||
                                                                  `Remove from ${currentEnvironmentLabel}`
                                                                : t(
                                                                      "secretsPanel.removeSecret"
                                                                  ) ||
                                                                  "Remove Secret"
                                                        }
                                                    >
                                                        <Unlink className="w-4 h-4" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>
                                                            {hasEnvironmentsFeature &&
                                                            !isContextProduction
                                                                ? t(
                                                                      "secretsPanel.removeSecretFromEnv"
                                                                  ) ||
                                                                  "Remove Secret Value"
                                                                : t(
                                                                      "secretsPanel.removeSecret"
                                                                  ) ||
                                                                  "Remove Secret"}
                                                        </AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            {hasEnvironmentsFeature &&
                                                            !isContextProduction
                                                                ? t(
                                                                      "secretsPanel.removeSecretFromEnvDescription",
                                                                      {
                                                                          name: secret.name,
                                                                          env: currentEnvironmentLabel,
                                                                      }
                                                                  ) ||
                                                                  `Are you sure you want to remove the value of "${secret.name}" for ${currentEnvironmentLabel}? The secret will still exist for other environments.`
                                                                : t(
                                                                      "secretsPanel.removeSecretDescription",
                                                                      {
                                                                          name: secret.name,
                                                                      }
                                                                  ) ||
                                                                  `Are you sure you want to remove "${secret.name}" from this workflow? The secret will not be deleted, only unlinked.`}
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>
                                                            {t(
                                                                "common.cancel"
                                                            ) || "Cancel"}
                                                        </AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() =>
                                                                handleRemoveSecret(
                                                                    secret.id,
                                                                    secret.name
                                                                )
                                                            }
                                                            className="bg-red-600 hover:bg-red-700"
                                                        >
                                                            {t(
                                                                "secretsPanel.remove"
                                                            ) || "Remove"}
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex flex-wrap gap-2">
                                        <Badge
                                            variant="secondary"
                                            className="text-xs"
                                        >
                                            <Check className="w-3 h-3 mr-1" />
                                            {t("secretsPanel.encrypted") ||
                                                "Encrypted"}
                                        </Badge>

                                        {/* Show environment badges - only if environments feature is available */}
                                        {hasEnvironmentsFeature && (
                                            <>
                                                {secret.hasProductionValue && (
                                                    <Badge
                                                        variant="outline"
                                                        className="text-xs bg-green-50 text-green-700 border-green-200"
                                                    >
                                                        <Globe className="w-3 h-3 mr-1" />
                                                        {t(
                                                            "secretsPanel.production"
                                                        ) || "Production"}
                                                    </Badge>
                                                )}

                                                {secret.environmentsWithValues?.map(
                                                    (env) => (
                                                        <Badge
                                                            key={env.id}
                                                            variant="outline"
                                                            className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                                                        >
                                                            <Server className="w-3 h-3 mr-1" />
                                                            {env.name
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                                env.name.slice(
                                                                    1
                                                                )}
                                                        </Badge>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </ScrollArea>

            {/* Create/Edit Secret Dialog */}
            <Dialog
                open={isCreateDialogOpen}
                onOpenChange={(open) => {
                    setIsCreateDialogOpen(open);
                    if (!open) resetCreateForm();
                }}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Key className="w-5 h-5 text-amber-600" />
                            {editingSecret
                                ? t("secretsPanel.editSecret") || "Edit Secret"
                                : t("secretsPanel.createNewSecret") ||
                                  "Create New Secret"}
                        </DialogTitle>
                        <DialogDescription>
                            {editingSecret
                                ? t("secretsPanel.editSecretDescription") ||
                                  "Update the secret value or description for a specific environment."
                                : t("secretsPanel.createSecretDescription") ||
                                  "Create a new encrypted secret and associate it with this workflow."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">
                                {t("secrets.secretName") || "Secret Name"}
                            </Label>
                            <Input
                                id="name"
                                name="name"
                                placeholder={
                                    t("secrets.namePlaceholder") || "MY_API_KEY"
                                }
                                value={formData.name}
                                onChange={handleInputChange}
                                className="font-mono"
                                disabled={!!editingSecret}
                            />
                            <p className="text-xs text-muted-foreground">
                                {t("secrets.nameHint") ||
                                    "Use UPPER_SNAKE_CASE format (e.g., STRIPE_API_KEY)"}
                            </p>
                            {errors.name && (
                                <p className="text-red-500 text-sm flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    {errors.name}
                                </p>
                            )}
                        </div>

                        {/* Environment Selector - only show if environments feature is available */}
                        {hasEnvironmentsFeature && (
                            <div className="space-y-2">
                                <Label htmlFor="environment">
                                    {t("secrets.environment") || "Environment"}
                                </Label>
                                <Select
                                    value={dialogEnvironment}
                                    onValueChange={setDialogEnvironment}
                                >
                                    <SelectTrigger>
                                        <SelectValue
                                            placeholder={
                                                t(
                                                    "secrets.selectEnvironment"
                                                ) || "Select environment..."
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {environmentOptions.map((env) => (
                                            <SelectItem
                                                key={env.id}
                                                value={env.id}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {env.id === "production" ? (
                                                        <Globe className="w-4 h-4 text-green-600" />
                                                    ) : (
                                                        <Server className="w-4 h-4 text-blue-600" />
                                                    )}
                                                    <span>{env.name}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    {dialogEnvironment === "production"
                                        ? t("secrets.productionHint") ||
                                          "Production is the default environment. This value will be used when no environment is specified."
                                        : t("secrets.environmentHint") ||
                                          "This value will only be used in this specific environment."}
                                </p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="value">
                                {t("secrets.secretValue") || "Secret Value"}
                            </Label>
                            <div className="relative">
                                <Input
                                    id="value"
                                    name="value"
                                    type={showValue ? "text" : "password"}
                                    placeholder={
                                        editingSecret
                                            ? t(
                                                  "secrets.valuePlaceholderEdit"
                                              ) ||
                                              "Enter new value to update..."
                                            : t("secrets.valuePlaceholder") ||
                                              "Enter secret value..."
                                    }
                                    value={formData.value}
                                    onChange={handleInputChange}
                                    className="pr-10 font-mono"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowValue(!showValue)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                                >
                                    {showValue ? (
                                        <EyeOff className="w-4 h-4 text-gray-500" />
                                    ) : (
                                        <Eye className="w-4 h-4 text-gray-500" />
                                    )}
                                </button>
                            </div>
                            {editingSecret && (
                                <p className="text-xs text-amber-600">
                                    {t("secrets.valueWarning") ||
                                        "Leave blank to keep existing value for this environment"}
                                </p>
                            )}
                            {errors.value && (
                                <p className="text-red-500 text-sm flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    {errors.value}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">
                                {t("secrets.secretDescription") ||
                                    "Description (Optional)"}
                            </Label>
                            <Textarea
                                id="description"
                                name="description"
                                placeholder={
                                    t("secrets.descriptionPlaceholder") ||
                                    "Brief description of what this secret is used for..."
                                }
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsCreateDialogOpen(false)}
                        >
                            {t("common.cancel") || "Cancel"}
                        </Button>
                        <Button
                            onClick={handleSaveSecret}
                            disabled={isCreating || isAssociating}
                        >
                            {isCreating || isAssociating ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : editingSecret ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="mr-2"
                                >
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                                    <polyline points="17 21 17 13 7 13 7 21" />
                                    <polyline points="7 3 7 8 15 8" />
                                </svg>
                            ) : (
                                <Plus className="w-4 h-4 mr-2" />
                            )}
                            {editingSecret
                                ? t("secretsPanel.updateSecret") ||
                                  "Update Secret"
                                : t("secretsPanel.createAndAssociate") ||
                                  "Create & Associate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Associate Existing Secret Dialog */}
            <Dialog
                open={isAssociateDialogOpen}
                onOpenChange={(open) => {
                    setIsAssociateDialogOpen(open);
                    if (!open) setSelectedSecretToAssociate("");
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Link2 className="w-5 h-5 text-blue-600" />
                            {t("secretsPanel.associateSecret") ||
                                "Associate Existing Secret"}
                        </DialogTitle>
                        <DialogDescription>
                            {t("secretsPanel.associateSecretDescription") ||
                                "Select a secret to associate with this workflow."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>
                                {t("secretsPanel.selectSecret") ||
                                    "Select Secret"}
                            </Label>
                            <Select
                                value={selectedSecretToAssociate}
                                onValueChange={setSelectedSecretToAssociate}
                            >
                                <SelectTrigger>
                                    <SelectValue
                                        placeholder={
                                            t(
                                                "secretsPanel.selectSecretPlaceholder"
                                            ) || "Choose a secret..."
                                        }
                                    />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableSecrets.map((secret) => (
                                        <SelectItem
                                            key={secret.id}
                                            value={secret.id}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Key className="w-4 h-4 text-amber-600" />
                                                <span className="font-mono text-sm">
                                                    {secret.name}
                                                </span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedSecretToAssociate && (
                                <p className="text-xs text-muted-foreground">
                                    {allSecrets.find(
                                        (s) =>
                                            s.id === selectedSecretToAssociate
                                    )?.description ||
                                        t("secrets.noDescription") ||
                                        "No description"}
                                </p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsAssociateDialogOpen(false)}
                        >
                            {t("common.cancel") || "Cancel"}
                        </Button>
                        <Button
                            onClick={handleAssociateSecret}
                            disabled={
                                isAssociating || !selectedSecretToAssociate
                            }
                        >
                            {isAssociating ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Link2 className="w-4 h-4 mr-2" />
                            )}
                            {t("secretsPanel.associate") || "Associate"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
